import React from "react";
import { useLocation } from "react-router-dom";
import ErrorPage from "../pages/ErrorPage";

const CHUNK_RECOVERY_PREFIX = "nuvelor:chunk-recovery:";
const CHUNK_RECOVERY_WINDOW_MS = 60_000;

function isRecoverableChunkError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("chunkloaderror") ||
    message.includes("loading chunk")
  );
}

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    if (!isRecoverableChunkError(error) || typeof window === "undefined") return;
    try {
      const key = `${CHUNK_RECOVERY_PREFIX}${this.props.resetKey || "root"}`;
      const now = Date.now();
      const last = Number(window.sessionStorage?.getItem(key) || 0);
      if (!last || now - last > CHUNK_RECOVERY_WINDOW_MS) {
        window.sessionStorage?.setItem(key, String(now));
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage type="500" />;
    }

    return this.props.children;
  }
}

export default function ErrorBoundary({ children }) {
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}${location.hash}`;

  return <ErrorBoundaryInner resetKey={resetKey}>{children}</ErrorBoundaryInner>;
}
